/**
 * Autocomplete Search Input Component
 * Searchable input with suggestions dropdown
 */
import { useState, useRef, useEffect } from "react";
import { Search, X } from "lucide-react";

export function AutocompleteInput({ 
  value, 
  onChange, 
  options, 
  placeholder = "Search...",
  displayKey = "name",
  valueKey = "id",
  label,
  required = false,
  emptyMessage = "No results found",
  noOptionsMessage = "No options available",
  className = ""
}) {
  const [inputValue, setInputValue] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState([]);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Find the display name for the current value
  useEffect(() => {
    if (value) {
      const selected = options.find(opt => opt[valueKey] === value);
      if (selected) {
        setInputValue(selected[displayKey]);
      }
    } else {
      setInputValue("");
    }
  }, [value, options, valueKey, displayKey]);

  // Filter options based on input
  useEffect(() => {
    if (inputValue.trim() === "") {
      setFilteredOptions(options.slice(0, 10)); // Show first 10 when empty
    } else {
      const filtered = options.filter(opt => 
        opt[displayKey].toLowerCase().includes(inputValue.toLowerCase())
      ).slice(0, 10); // Limit to 10 results
      setFilteredOptions(filtered);
    }
  }, [inputValue, options, displayKey]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
    setIsOpen(true);
    // Clear selection if user is typing something different
    if (value) {
      const selected = options.find(opt => opt[valueKey] === value);
      if (selected && selected[displayKey] !== e.target.value) {
        onChange("", "");
      }
    }
  };

  const handleSelect = (option) => {
    setInputValue(option[displayKey]);
    onChange(option[valueKey], option[displayKey]);
    setIsOpen(false);
  };

  const handleClear = () => {
    setInputValue("");
    onChange("", "");
    inputRef.current?.focus();
  };

  return (
    <div className={`space-y-2 ${className}`} ref={containerRef}>
      {label && (
        <label className="text-slate-300 text-sm font-medium">
          {label} {required && <span className="text-red-400">*</span>}
        </label>
      )}
      <div className="relative">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onFocus={() => setIsOpen(true)}
            placeholder={placeholder}
            className="w-full h-10 pl-10 pr-10 rounded-md bg-slate-800/50 border border-slate-700 text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500"
            required={required && !value}
          />
          {inputValue && (
            <button
              type="button"
              onClick={handleClear}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Dropdown */}
        {isOpen && (
          <div className="absolute z-50 w-full mt-1 bg-[#1a2130] border border-slate-600 rounded-lg shadow-xl max-h-60 overflow-y-auto">
            {options.length === 0 ? (
              <div className="px-4 py-3 text-slate-400 text-sm">
                {noOptionsMessage}
              </div>
            ) : filteredOptions.length === 0 ? (
              <div className="px-4 py-3 text-slate-400 text-sm">
                {emptyMessage}
              </div>
            ) : (
              filteredOptions.map((option) => (
                <button
                  key={option[valueKey]}
                  type="button"
                  onClick={() => handleSelect(option)}
                  className={`w-full text-left px-4 py-2.5 hover:bg-slate-700/50 transition-colors ${
                    value === option[valueKey] 
                      ? "bg-cyan-500/10 text-cyan-400" 
                      : "text-slate-200"
                  }`}
                >
                  <span className="font-medium">{option[displayKey]}</span>
                  {option.site_count !== undefined && (
                    <span className="text-slate-500 text-sm ml-2">({option.site_count} sites)</span>
                  )}
                  {option.student_count !== undefined && (
                    <span className="text-slate-500 text-sm ml-2">({option.student_count} students)</span>
                  )}
                </button>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}
